import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

// Create axios instance
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Auth token management
let authToken: string | null = null

export const setAuthToken = (token: string) => {
  authToken = token
}

export const clearAuthToken = () => {
  authToken = null
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (authToken && config.headers) {
      config.headers.Authorization = `Bearer ${authToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // If 401 and not a retry, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refresh_token')
        if (refreshToken) {
          const response = await api.post('/api/v1/auth/refresh', {
            refresh_token: refreshToken,
          })

          const { access_token, refresh_token: newRefreshToken } = response.data
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('refresh_token', newRefreshToken)
          setAuthToken(access_token)

          // Retry original request
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`
          }
          return api(originalRequest)
        }
      } catch {
        // Refresh failed, clear auth
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
        clearAuthToken()
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// API service functions
export const authService = {
  sendMagicLink: (email: string, userType: 'expert' | 'company') =>
    api.post('/api/v1/auth/magic-link', { email, user_type: userType }),

  verifyMagicLink: (token: string, userType: string) =>
    api.post(`/api/v1/auth/verify?type=${userType}`, { token }),

  refresh: (refreshToken: string) =>
    api.post('/api/v1/auth/refresh', { refresh_token: refreshToken }),

  logout: (refreshToken: string) =>
    api.post('/api/v1/auth/logout', { refresh_token: refreshToken }),
}

export const profileService = {
  getCurrentProfile: () => api.get('/api/v1/profiles/me'),

  updateProfile: (data: Record<string, unknown>) => api.put('/api/v1/profiles/me', data),

  deleteProfile: () => api.delete('/api/v1/profiles/me'),
}

export const expertService = {
  list: (params?: Record<string, unknown>) => api.get('/api/v1/experts', { params }),

  getById: (id: string) => api.get(`/api/v1/experts/${id}`),

  getPublicProfile: (id: string) => api.get(`/api/v1/experts/${id}/public`),
}

export const companyService = {
  list: (params?: Record<string, unknown>) => api.get('/api/v1/companies', { params }),

  getById: (id: string) => api.get(`/api/v1/companies/${id}`),

  getPublicProfile: (id: string) => api.get(`/api/v1/companies/${id}/public`),
}

export const assessmentService = {
  // Start a new assessment for a skill
  start: (skillId: string) => api.post('/api/v1/assessments', { skill_id: skillId }),

  // Get the next question for an assessment
  getNextQuestion: (assessmentId: string) => api.get(`/api/v1/assessments/${assessmentId}/question`),

  // Submit an answer
  submitAnswer: (
    assessmentId: string,
    questionId: string,
    selectedOptions: string[],
    presentedAt: string
  ) =>
    api.post(`/api/v1/assessments/${assessmentId}/answer`, {
      question_id: questionId,
      selected_options: selectedOptions,
      presented_at: presentedAt,
    }),

  // Get assessment status
  getStatus: (assessmentId: string) => api.get(`/api/v1/assessments/${assessmentId}`),

  // List user's assessments
  list: (params?: { skill_id?: string; status?: string }) =>
    api.get('/api/v1/assessments', { params }),
}

export const homepageService = {
  // Get aggregated homepage data
  getData: () => api.get('/api/v1/homepage'),
}
